import React from 'react';
import ReactGA from 'react-ga4';
import axios from 'axios';
import { Provider } from 'react-redux';
import { PersistGate } from 'redux-persist/lib/integration/react';
import configureStore from './store/configureStore';
import { ToastContainer } from 'react-toastify';
import AppTour from './components/AppTour';
import Loader from './components/Loader/Loader';

// Main Style
import './App.scss';
// react-bootstrap-typeahead css style
import 'react-bootstrap-typeahead/css/Typeahead.css';

import AppRouter, { history } from './routers/AppRouter';
import { startLoginUsingToken, startLogout } from './actions/auth';
import { startSetProfile } from './actions/profile';
import { AppProvider } from './AppContext';
import { setThrottled } from './actions/app';

// Set redux store
const { persistor, store } = configureStore();

// Global Axios Interceptor for Throttling
axios.interceptors.response.use(
  response => response,
  error => {
    const { response } = error;
    if (
      response &&
      (response.status === 409 || response.status === 429) &&
      response.data &&
      response.data.errors &&
      response.data.errors.some(err => err.code === 'throttled')
    ) {
      const throttledError = response.data.errors.find(
        err => err.code === 'throttled'
      );
      const detail = throttledError.detail || '';
      const match = detail.match(/(\d+)\s+seconds/);
      const retryAfter = match ? parseInt(match[1], 10) : 30;
      const path = history.location.pathname;

      store.dispatch(setThrottled(retryAfter, path));
    }
    return Promise.reject(error);
  }
);

// This will be used by other components
const appData = {
  apiBaseUrl: '',
  initialPath: '',
  helpTextUrl: ''
};

// Set API Base URL
const reactAppEnv = process.env.REACT_APP_ENV;
appData.apiBaseUrl = process.env.REACT_APP_API_URL || '';

if (reactAppEnv !== 'TEST') {
  ReactGA.initialize([
    {
      trackingId: 'UA-148178702-2'
    }
  ]);

  history.listen(location => {
    ReactGA.set({ page: location.pathname }); // Update the user's current page
    ReactGA.send({
      hitType: 'pageview',
      page: location.pathname,
      title: location.pathname
    }); // Record a pageview for the given page
  });
}

if (!appData.apiBaseUrl) console.error('API base URL not set.');

// Get authentication token from redux store
const getAuthToken = () => {
  return store.getState().auth.uid || '';
};

// Get GroupId
export const getGroupId = () => {
  return store.getState().profile.profileData.group_id || '';
};

// Get active language from redux store
const getLocale = () => {
  return store.getState().locale;
};

// Get translations redux store
const getActiveLanguage = () => {
  return store.getState().settings.lang || 'en';
};

// Get Active language but valid for Accept-Language header and other formats
const getActiveLanguageHeader = () => {
  const activeLanguage = getActiveLanguage();
  switch (activeLanguage) {
    case 'en':
      return 'en-gb';
    case 'fr':
      return 'fr';
    case 'nl':
      return 'nl-BE';
    default:
      return 'en-gb';
  }
};

const setInitialPath = (path = '') => {
  appData.initialPath = path;
};

class App extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      displayApp: false
    };

    this.renderLoginPage = this.renderLoginPage.bind(this);
    this.renderDashboardPage = this.renderDashboardPage.bind(this);
    this.onBeforeLift = this.onBeforeLift.bind(this);
  }

  renderLoginPage() {
    // Store initial user path
    const userPath = history.location.pathname;
    const userSearch = history.location.search;

    setInitialPath(userPath + userSearch);

    // Destroy all previous user data
    store.dispatch(startLogout());

    this.setState({ displayApp: true });

    if (userPath.indexOf('/reset_password') !== -1) {
      history.push({
        pathname: userPath,
        search: userSearch
      });
    } else {
      history.push('/');
    }
  }

  renderDashboardPage() {
    this.setState(() => ({ displayApp: true }));

    if (history.location.pathname === '/') {
      history.push('/dashboard');
    }

    // const userName = store.getState().profile.profileData.username || '';
  }

  onBeforeLift() {
    const userPath = history.location.pathname;
    const tokenLength = 40; // Default auth token length
    const authToken = getAuthToken();
    const groupId = getGroupId();

    if (userPath.indexOf('impersonate') !== -1) {
      const parsedToken = userPath.split('/').pop();

      if (parsedToken && parsedToken.length === tokenLength) {
        store
          .dispatch(startLoginUsingToken(parsedToken))
          .then(() => {
            // Store initial user path
            const userPath = history.location.pathname;
            setInitialPath(userPath);

            this.setState({ displayApp: true });

            this.renderDashboardPage();
          })
          .catch(() => this.renderLoginPage());
      } else {
        this.renderLoginPage();
      }

      return;
    }

    if (authToken || groupId) {
      store
        .dispatch(startSetProfile(authToken, groupId))
        .then(() => this.renderDashboardPage())
        .catch(() => this.renderLoginPage());
    } else {
      this.renderLoginPage();
    }
  }

  render() {
    const { displayApp } = this.state;
    return (
      <div>
        <Provider store={store}>
          <PersistGate
            loading={<Loader loaderOffset={0} />}
            onBeforeLift={this.onBeforeLift}
            persistor={persistor}
          >
            {displayApp ? (
              <AppProvider getActiveLanguage={getActiveLanguage}>
                <div>
                  <AppRouter />
                  <AppTour />
                  <ToastContainer position="top-right" autoClose={10000} />
                </div>
              </AppProvider>
            ) : (
              <Loader loaderOffset={0} />
            )}
          </PersistGate>
        </Provider>
      </div>
    );
  }
}

export {
  App,
  getAuthToken,
  getActiveLanguageHeader,
  getLocale,
  setInitialPath,
  appData
};
